import { formatDateString } from '~libs/dayjs';

import { Column, Row } from '~components/table-view';
import CareRecipientActions from '~support-at-home/components/config/care-recipients/CareRecipientActions';
import CareRecipientClientCell from '~support-at-home/components/config/care-recipients/CareRecipientClientCell';
import CareRecipientStatusBadge from '~support-at-home/components/config/care-recipients/CareRecipientStatusBadge';
import TruncatedCell from '~support-at-home/components/config/care-recipients/TruncatedCell';

import { CARE_RECIPIENT_GENDER_NAMES, CareRecipient } from '~support-at-home/models/careRecipients';

interface ColumnFactoryCallbacks {
  onLink?: (careRecipient: CareRecipient) => void;
  onIgnore?: (careRecipient: CareRecipient) => void;
  onRestore?: (careRecipient: CareRecipient) => void;
  onUnlink?: (careRecipient: CareRecipient) => void;
  isBusy?: boolean;
}

export const formatGender = (code: string | null | undefined): string => {
  if (!code) return '-';
  return CARE_RECIPIENT_GENDER_NAMES[code.toUpperCase()] ?? '-';
};

// A bare calendar date from Services Australia (`YYYY-MM-DD`): formatted as written, with no timezone shift.
export const formatBirthDate = (value: string | null | undefined): string => formatDateString(value) || '-';

// Rows stay on one line: long values are cut short with an ellipsis and show in full on hover, and a
// table wider than the tab scrolls inside its own box (see .sah-care-recipients in _configs.scss).
const text = (value: string | null | undefined) => ({ text: value || '-' });

export const columnsFactory = (callbacks: ColumnFactoryCallbacks = {}): Column[] => [
  {
    id: 'care_recipient_id',
    header: 'CR ID',
    caption: 'Care Recipient ID',
    type: 'custom',
    sortable: true,
    component: TruncatedCell,
    componentProps: (row: Row) => text(row.care_recipient_id),
  },
  {
    id: 'name',
    header: 'Name',
    type: 'custom',
    sortable: true,
    component: TruncatedCell,
    componentProps: (row: Row) => text(row.name),
  },
  {
    id: 'birth_date',
    header: 'DOB',
    caption: 'Date of birth',
    type: 'custom',
    sortable: true,
    component: TruncatedCell,
    componentProps: (row: Row) => text(formatBirthDate(row.birth_date)),
  },
  {
    id: 'gender',
    header: 'Gender',
    type: 'custom',
    sortable: false,
    component: TruncatedCell,
    componentProps: (row: Row) => text(formatGender(row.gender)),
  },
  {
    id: 'match_status',
    header: 'Status',
    type: 'custom',
    sortable: false,
    component: CareRecipientStatusBadge,
    classNames: {
      header: 'cell-align-center',
      column: 'cell-align-center',
    },
  },
  {
    id: 'linked_client',
    header: 'TurnPoint Client',
    caption: 'The client whose Medicare Ref. matches this care recipient',
    type: 'custom',
    sortable: false,
    component: CareRecipientClientCell,
  },
  {
    id: 'actions',
    header: '',
    type: 'custom',
    sortable: false,
    component: CareRecipientActions,
    componentProps: callbacks,
    classNames: {
      header: 'cell-align-right',
      column: 'cell-align-right',
    },
  },
];

export type CareRecipientRow = Row & CareRecipient;
